import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    n: "01",
    title: "Removable Prosthodontics",
    desc: "Complete, cast partial and flexible dentures engineered for retention, comfort and lifelike esthetics.",
    img: "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    n: "02",
    title: "Implant Surgery & Prosthetic Rehabilitation",
    desc: "Guided, prosthetically-driven implant placement restoring function from single teeth to full arches.",
    img: "https://images.pexels.com/photos/38362421/pexels-photo-38362421.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    n: "03",
    title: "Full Mouth Rehabilitation",
    desc: "Systematic reconstruction of worn or collapsed dentitions with a controlled occlusal philosophy.",
    img: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    n: "04",
    title: "Smile Designing & Esthetic Restorative",
    desc: "Digital smile design, veneers and conservative restorations tailored to your facial harmony.",
    img: "https://images.unsplash.com/photo-1489278353717-f64c6ee8a4d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhZXN0aGV0aWMlMjBzbWlsZSUyMGZlbWFsZXxlbnwwfHx8fDE3ODUxNjE2MDd8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    n: "05",
    title: "TMJ Management",
    desc: "Diagnosis and management of temporomandibular disorders, bruxism and occlusal dysfunction.",
    img: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    n: "06",
    title: "Maxillofacial Prosthesis",
    desc: "Custom prostheses restoring form and function following surgical or congenital defects.",
    img: "https://images.unsplash.com/photo-1609840114035-3c981b782dfe?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
];

export const Services = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="bg-ivory-darker py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <Reveal>
              <p className="text-champagne-dark text-xs uppercase tracking-[0.3em] mb-6">
                Areas of Expertise
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-emerald leading-[1.05] max-w-2xl">
                A complete spectrum of prosthodontic care.
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="relative">
          {SERVICES.map((s) => (
            <div
              key={s.n}
              data-testid={`service-row-${s.n}`}
              onMouseEnter={() => setActive(s.n)}
              onMouseLeave={() => setActive(null)}
              className="group relative border-t border-emerald/15 last:border-b py-8 md:py-10 cursor-default transition-colors duration-300 hover:bg-ivory"
            >
              <div className="flex items-start md:items-center gap-6 md:gap-12 px-2 md:px-6">
                <span className="font-display text-champagne-dark text-lg md:text-2xl w-10 shrink-0">
                  {s.n}
                </span>
                <div className="flex-1">
                  <h3 className="font-display font-light text-2xl md:text-4xl text-emerald tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-emerald/60 text-sm md:text-base max-w-xl leading-relaxed">
                    {s.desc}
                  </p>
                </div>
                <ArrowUpRight
                  className="text-champagne-dark opacity-0 group-hover:opacity-100 transition-opacity duration-300 shrink-0"
                  size={28}
                />
              </div>
            </div>
          ))}

          {/* Floating preview image (desktop) */}
          <AnimatePresence>
            {active && (
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="hidden lg:block pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 w-72 h-80 z-20 shadow-[0_20px_40px_rgba(11,43,33,0.18)]"
              >
                <img
                  src={SERVICES.find((x) => x.n === active)?.img}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 border border-champagne/40" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Services;
